/**
 * Page éditeur plan de table — RESPONSIVE
 * Desktop : 3 colonnes (outils | canvas | invités)
 * Tablette : canvas + tiroirs latéraux
 * Mobile   : canvas plein écran + barre bas + drawers
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Save, Share2, Printer, Users,
  ChevronLeft, ChevronRight, PanelLeft, PanelRight,
  Maximize2, Settings2, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { PlanProvider, usePlan } from '../components/plan-table/PlanContext';
import PlanCanvas      from '../components/plan-table/PlanCanvas';
import ToolsPanel      from '../components/plan-table/ToolsPanel';
import GuestsSidePanel from '../components/plan-table/GuestsSidePanel';
import ZoomControls    from '../components/plan-table/ZoomControls';
import PrintModal      from '../components/plan-table/PrintModal';
import { useAuth }     from '../context/AuthContext';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// ── Animation CSS ────────────────────────────────────────────────────────────
function InjectCSS() {
  useEffect(() => {
    const id = 'pdt-editor-styles';
    if (document.getElementById(id)) return;
    const el = document.createElement('style');
    el.id = id;
    el.textContent = `
      @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700&display=swap');
      @keyframes seat-pulse {
        0%, 100% { box-shadow: 0 0 0 2px rgba(212,175,55,0.3); transform: scale(1.1); }
        50%       { box-shadow: 0 0 0 6px rgba(212,175,55,0.1); transform: scale(1.2); }
      }
      @keyframes slideInLeft  { from { transform: translateX(-100%); } to { transform: translateX(0); } }
      @keyframes slideInRight { from { transform: translateX(100%); }  to { transform: translateX(0); } }
      @keyframes slideInUp    { from { transform: translateY(100%); }  to { transform: translateY(0); } }
      .pdt-drawer-left  { animation: slideInLeft  .22s ease; }
      .pdt-drawer-right { animation: slideInRight .22s ease; }
      .pdt-drawer-up    { animation: slideInUp    .22s ease; }
    `;
    document.head.appendChild(el);
  }, []);
  return null;
}

// ── Toolbar ──────────────────────────────────────────────────────────────────
function EditorToolbar({ planToken, onSave, saving, isMobile, onToggleTools, onToggleGuests }) {
  const { planData, resolvedTables, invites, fitToScreen } = usePlan();
  const [printOpen, setPrintOpen] = useState(false);

  const placed = invites?.filter(i => i.table_id).length || 0;
  const total  = invites?.length || 0;
  const tables = resolvedTables?.length || 0;

  const shareUrl = `${window.location.origin}/plan-de-table/${planToken}`;
  const copyLink = () => {
    navigator.clipboard?.writeText(shareUrl)
      .then(() => toast.success('Lien copié !'))
      .catch(() => toast.error('Impossible de copier'));
  };

  return (
    <>
      <div style={{
        height: 52, flexShrink: 0, display: 'flex', alignItems: 'center',
        gap: 8, padding: '0 12px',
        background: '#0B1E20', borderBottom: '2px solid #D4AF37',
        fontFamily: "'Montserrat', sans-serif",
        zIndex: 100,
      }}>
        <Link to="/mon-organisation"
          style={{ color: '#D4AF37', display: 'flex', alignItems: 'center', gap: 3,
            textDecoration: 'none', fontSize: 11, fontWeight: 600, flexShrink: 0 }}>
          <ArrowLeft size={14} /> <span className="hidden sm:inline">Retour</span>
        </Link>

        <div style={{ width: 1, height: 22, background: 'rgba(255,255,255,.12)', flexShrink: 0 }} />

        {/* Titre + stats */}
        <span style={{ color: '#FFFFFF', fontSize: 12, fontWeight: 700,
          flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {planData?.nom_evenement || 'Plan de table'}
        </span>
        <span style={{ fontSize: 10, color: 'rgba(255,255,255,.45)', whiteSpace: 'nowrap', flexShrink: 0 }}>
          {placed}/{total} · {tables} tables
        </span>

        {/* Boutons actions */}
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          {/* Mobile : boutons outils + invités */}
          {isMobile && (
            <>
              <ToolBtn onClick={onToggleTools} title="Outils"><PanelLeft size={14} /></ToolBtn>
              <ToolBtn onClick={onToggleGuests} title="Invités"><Users size={14} /></ToolBtn>
            </>
          )}

          <ToolBtn onClick={fitToScreen} title="Ajuster à l'écran"><Maximize2 size={14} /></ToolBtn>
          <ToolBtn onClick={copyLink} title="Partager"><Share2 size={14} /></ToolBtn>
          <ToolBtn onClick={() => setPrintOpen(true)} title="Imprimer"><Printer size={14} /></ToolBtn>

          {/* Sauvegarder */}
          <button
            onClick={onSave}
            disabled={saving}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              background: '#D4AF37', color: '#000',
              border: 'none', borderRadius: 7, padding: '6px 12px',
              fontSize: 11, fontWeight: 700, cursor: 'pointer',
              opacity: saving ? 0.6 : 1,
              fontFamily: "'Montserrat',sans-serif",
            }}>
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
            <span className="hidden sm:inline">{saving ? '…' : 'Sauvegarder'}</span>
          </button>
        </div>
      </div>

      {printOpen && planData && (
        <PrintModal
          plan={planData}
          invites={invites}
          tables={resolvedTables}
          groups={planData.groups || []}
          onClose={() => setPrintOpen(false)}
        />
      )}
    </>
  );
}

function ToolBtn({ onClick, title, children }) {
  return (
    <button onClick={onClick} title={title} style={{
      width: 32, height: 32, borderRadius: 7, border: 'none',
      background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)',
      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {children}
    </button>
  );
}

// ── Composant interne (a accès au PlanContext) ────────────────────────────────
function PlanEditorInner({ planToken }) {
  const { loading, fetchPlan, fitToScreen } = usePlan();
  const { token: authToken } = useAuth();
  const [saving, setSaving] = useState(false);

  // Responsive
  const [isMobile,  setIsMobile]  = useState(window.innerWidth < 768);
  const [isTablet,  setIsTablet]  = useState(window.innerWidth >= 768 && window.innerWidth < 1100);
  const [showTools, setShowTools] = useState(false);
  const [showGuests, setShowGuests] = useState(false);
  const [toolsCollapsed, setToolsCollapsed] = useState(false);
  const [guestsCollapsed, setGuestsCollapsed] = useState(false);

  useEffect(() => {
    function onResize() {
      const w = window.innerWidth;
      setIsMobile(w < 768);
      setIsTablet(w >= 768 && w < 1100);
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Fit to screen au chargement
  useEffect(() => {
    if (!loading) {
      setTimeout(fitToScreen, 200);
    }
  }, [loading, fitToScreen]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await fetchPlan();
      toast.success('Plan sauvegardé !');
    } catch {
      toast.error('Erreur sauvegarde');
    } finally {
      setSaving(false);
    }
  }, [fetchPlan]);

  if (loading) {
    return (
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#FEFAF5', fontFamily: "'Montserrat',sans-serif",
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', border: '3px solid #E5E7EB',
            borderTopColor: '#D4AF37', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          <p style={{ color: '#9CA3AF', fontSize: 13 }}>Chargement du plan…</p>
        </div>
      </div>
    );
  }

  // ── LAYOUT DESKTOP (≥ 1100px) ─────────────────────────────────────────────
  if (!isMobile && !isTablet) {
    return (
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>
        {/* Panneau outils gauche */}
        {!toolsCollapsed ? (
          <ToolsPanel collapsed={false} onToggle={() => setToolsCollapsed(true)} />
        ) : (
          <button
            onClick={() => setToolsCollapsed(false)}
            title="Ouvrir les outils"
            style={{
              position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)',
              zIndex: 30, background: '#fff', border: '1px solid #E5E7EB',
              borderLeft: 'none', borderRadius: '0 8px 8px 0',
              padding: '10px 5px', cursor: 'pointer', color: '#374151',
              boxShadow: '2px 0 8px rgba(0,0,0,0.08)', fontSize: 12,
            }}>
            ▶
          </button>
        )}

        {/* Canvas central */}
        <div style={{ flex: 1, position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <PlanCanvas />
          <ZoomControls />
        </div>

        {/* Panneau invités droit */}
        {!guestsCollapsed ? (
          <GuestsSidePanel style={{ width: 260, flexShrink: 0 }} />
        ) : (
          <button
            onClick={() => setGuestsCollapsed(false)}
            title="Ouvrir les invités"
            style={{
              position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)',
              zIndex: 30, background: '#fff', border: '1px solid #E5E7EB',
              borderRight: 'none', borderRadius: '8px 0 0 8px',
              padding: '10px 5px', cursor: 'pointer', color: '#374151',
              boxShadow: '-2px 0 8px rgba(0,0,0,0.08)', fontSize: 12,
            }}>
            ◀
          </button>
        )}
      </div>
    );
  }

  // ── LAYOUT TABLETTE (768-1100px) ──────────────────────────────────────────
  if (isTablet) {
    return (
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>
        {/* Canvas */}
        <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
          <PlanCanvas />
          <ZoomControls />
        </div>

        {/* Drawer outils - overlay gauche */}
        {showTools && (
          <div style={{
            position: 'absolute', left: 0, top: 0, bottom: 0, zIndex: 50,
            display: 'flex',
          }}
            onClick={e => { if (e.target === e.currentTarget) setShowTools(false); }}>
            <div className="pdt-drawer-left" style={{ background: '#fff', boxShadow: '4px 0 24px rgba(0,0,0,0.15)' }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 10px 0' }}>
                <button onClick={() => setShowTools(false)} style={closeBtn}>✕</button>
              </div>
              <ToolsPanel collapsed={false} onToggle={() => setShowTools(false)} />
            </div>
            <div style={{ flex: 1, background: 'rgba(0,0,0,0.3)' }} onClick={() => setShowTools(false)} />
          </div>
        )}

        {/* Drawer invités - overlay droit */}
        {showGuests && (
          <div style={{
            position: 'absolute', right: 0, top: 0, bottom: 0, zIndex: 50,
            display: 'flex',
          }}>
            <div style={{ flex: 1, background: 'rgba(0,0,0,0.3)' }} onClick={() => setShowGuests(false)} />
            <div className="pdt-drawer-right" style={{ background: '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,0.15)', width: 280 }}>
              <div style={{ display: 'flex', justifyContent: 'flex-start', padding: '8px 10px 0' }}>
                <button onClick={() => setShowGuests(false)} style={closeBtn}>✕</button>
              </div>
              <GuestsSidePanel />
            </div>
          </div>
        )}

        {/* Barre latérale tablette */}
        <div style={{
          position: 'absolute', right: 12, bottom: 80, zIndex: 40,
          display: 'flex', flexDirection: 'column', gap: 6,
        }}>
          <button onClick={() => { setShowTools(t => !t); setShowGuests(false); }}
            title="Outils" style={{ ...fabBtn, background: showTools ? '#D4AF37' : '#fff' }}>
            <Settings2 size={18} style={{ color: showTools ? '#000' : '#374151' }} />
          </button>
          <button onClick={() => { setShowGuests(g => !g); setShowTools(false); }}
            title="Invités" style={{ ...fabBtn, background: showGuests ? '#D4AF37' : '#fff' }}>
            <Users size={18} style={{ color: showGuests ? '#000' : '#374151' }} />
          </button>
        </div>
      </div>
    );
  }

  // ── LAYOUT MOBILE (< 768px) ───────────────────────────────────────────────
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
      {/* Canvas plein écran */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        <PlanCanvas />
        <ZoomControls />
      </div>

      {/* Barre du bas (mobile) */}
      <div style={{
        height: 56, flexShrink: 0, display: 'flex', alignItems: 'center',
        background: '#fff', borderTop: '1px solid #E5E7EB',
        padding: '0 12px', gap: 8, zIndex: 40,
      }}>
        <button onClick={() => { setShowTools(t => !t); setShowGuests(false); }}
          style={{ ...mobileTab, background: showTools ? '#D4AF371A' : 'transparent', color: showTools ? '#C58B19' : '#6B7280' }}>
          <Settings2 size={20} />
          <span style={{ fontSize: 9 }}>Outils</span>
        </button>
        <button onClick={() => { setShowGuests(g => !g); setShowTools(false); }}
          style={{ ...mobileTab, background: showGuests ? '#D4AF371A' : 'transparent', color: showGuests ? '#C58B19' : '#6B7280' }}>
          <Users size={20} />
          <span style={{ fontSize: 9 }}>Invités</span>
        </button>
        <div style={{ flex: 1 }} />
        <button onClick={handleSave} disabled={saving}
          style={{ background: '#D4AF37', color: '#000', border: 'none', borderRadius: 8,
            padding: '8px 14px', fontSize: 11, fontWeight: 700, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 4,
            fontFamily: "'Montserrat',sans-serif" }}>
          <Save size={13} /> {saving ? '…' : 'Sauver'}
        </button>
      </div>

      {/* Drawer outils (bas, mobile) */}
      {showTools && (
        <div style={{
          position: 'absolute', left: 0, right: 0, bottom: 56, zIndex: 50,
          maxHeight: '65vh', display: 'flex', flexDirection: 'column',
          background: '#fff', boxShadow: '0 -4px 24px rgba(0,0,0,0.12)',
          borderRadius: '16px 16px 0 0', overflow: 'hidden',
        }} className="pdt-drawer-up">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 14px 0', borderBottom: '1px solid #F3F4F6', paddingBottom: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#374151', fontFamily: "'Montserrat',sans-serif" }}>Outils</span>
            <button onClick={() => setShowTools(false)} style={closeBtn}>✕</button>
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            <ToolsPanel collapsed={false} onToggle={() => setShowTools(false)} />
          </div>
        </div>
      )}

      {/* Drawer invités (bas, mobile) */}
      {showGuests && (
        <div style={{
          position: 'absolute', left: 0, right: 0, bottom: 56, zIndex: 50,
          maxHeight: '75vh', display: 'flex', flexDirection: 'column',
          background: '#fff', boxShadow: '0 -4px 24px rgba(0,0,0,0.12)',
          borderRadius: '16px 16px 0 0', overflow: 'hidden',
        }} className="pdt-drawer-up">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 14px 0', borderBottom: '1px solid #F3F4F6', paddingBottom: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#374151', fontFamily: "'Montserrat',sans-serif" }}>Invités</span>
            <button onClick={() => setShowGuests(false)} style={closeBtn}>✕</button>
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            <GuestsSidePanel />
          </div>
        </div>
      )}
    </div>
  );
}

// Styles utilitaires
const closeBtn = {
  background: 'none', border: 'none', cursor: 'pointer',
  color: '#9CA3AF', fontSize: 14, padding: '2px 6px',
  borderRadius: 6, fontFamily: "'Montserrat',sans-serif",
};
const fabBtn = {
  width: 42, height: 42, borderRadius: 12, border: '1px solid #E5E7EB',
  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
  boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
};
const mobileTab = {
  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
  border: 'none', borderRadius: 8, padding: '4px 12px', cursor: 'pointer',
  fontFamily: "'Montserrat',sans-serif",
};

// ── Page principale ───────────────────────────────────────────────────────────
export default function PlanEditorPage() {
  const { user, token } = useAuth();
  const [planToken, setPlanToken] = useState(null);
  const [notFound, setNotFound]   = useState(false);
  const [isMobile, setIsMobile]   = useState(window.innerWidth < 768);

  useEffect(() => {
    function onResize() { setIsMobile(window.innerWidth < 768); }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!user || !token) return;
    fetch(`${BACKEND}/api/mon-plan-de-table`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => {
        const plans = d.plans || [];
        if (plans[0]?.token) setPlanToken(plans[0].token);
        else setNotFound(true);
      })
      .catch(() => setNotFound(true));
  }, [user, token]);

  if (notFound) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        background: '#FEFAF5', fontFamily: "'Montserrat',sans-serif" }}>
        <p style={{ fontWeight: 700, color: '#374151', marginBottom: 12 }}>Aucun plan de table trouvé.</p>
        <Link to="/mon-organisation"
          style={{ color: '#D4AF37', textDecoration: 'none', fontSize: 13 }}>
          ← Retour à mon organisation
        </Link>
      </div>
    );
  }

  if (!planToken) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center',
        justifyContent: 'center', background: '#FEFAF5' }}>
        <div style={{ width: 36, height: 36, borderRadius: '50%',
          border: '3px solid #E5E7EB', borderTopColor: '#D4AF37',
          animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  return (
    <PlanProvider planToken={planToken} authToken={token}>
      <InjectCSS />
      <div style={{ height: '100dvh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <PlanEditorInnerWrapper planToken={planToken} isMobile={isMobile} />
      </div>
    </PlanProvider>
  );
}

function PlanEditorInnerWrapper({ planToken, isMobile }) {
  const { loading, fetchPlan } = usePlan();
  const [saving, setSaving] = useState(false);
  const [showTools,  setShowTools]  = useState(false);
  const [showGuests, setShowGuests] = useState(false);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await fetchPlan();
      toast.success('Plan sauvegardé !');
    } catch { toast.error('Erreur'); }
    setSaving(false);
  }, [fetchPlan]);

  return (
    <>
      <EditorToolbar
        planToken={planToken}
        onSave={handleSave}
        saving={saving}
        isMobile={isMobile}
        onToggleTools={() => { setShowTools(t => !t); setShowGuests(false); }}
        onToggleGuests={() => { setShowGuests(g => !g); setShowTools(false); }}
      />
      <PlanEditorInner
        planToken={planToken}
        saving={saving}
        setSaving={setSaving}
        externalShowTools={showTools}
        externalShowGuests={showGuests}
        setExternalShowTools={setShowTools}
        setExternalShowGuests={setShowGuests}
      />
    </>
  );
}
